import type {
  ChangePropagation,
  DomainOwner,
  RelationshipKind,
  SafeguardKind,
  SubscriptionRelationshipProfile,
} from './consumerContractRegistry.types';

export const PUBLIC_PDF_MAPPER = 'Public PDF mapper';
export const SHARED_SUMMARY_BUILDER = 'getDesignerSummaryRows';

export const RELATIONSHIP_PRESETS = {
  sharedBuilderAutomatic: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale:
      'Field formatting follows the shared getDesignerSummaryRows builder used across staff and internal views. Canonical field changes propagate automatically when the shared builder is updated.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  sharedBuilderQuoteField: {
    domainOwner: 'quote' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale:
      'Quote metadata row emitted by getDesignerSummaryRows. Updates to the shared builder propagate automatically.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  publicPdfMapperField: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'generated_from_shared_contract' as ChangePropagation,
    propagationRationale:
      'Intentional segregated boundary: Public customer PDF is rendered by the public-doc Supabase Edge Function with its own mapper against sanitised meta_json. This registry documents the contract; deploys are separate by design, not drift.',
    safeguard: 'contract_parity_test' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  publicPdfMapperQuoteField: {
    domainOwner: 'quote' as DomainOwner,
    changePropagation: 'generated_from_shared_contract' as ChangePropagation,
    propagationRationale:
      'Quote metadata rows for the public PDF follow the Edge Function mapper declared in this registry. Separate deploy boundary is intentional.',
    safeguard: 'contract_parity_test' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  publicPdfMapperAggregate: {
    domainOwner: 'derived_cache' as DomainOwner,
    changePropagation: 'generated_from_shared_contract' as ChangePropagation,
    propagationRationale:
      'Summary rows for public PDF are built inline in public-doc/index.ts rather than calling getDesignerSummaryRows — an intentional Edge Function boundary.',
    safeguard: 'contract_parity_test' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  directAutomatic: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale: 'Direct read from declared path; no separate mapper fork verified.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  directQuoteAutomatic: {
    domainOwner: 'quote' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale: 'Direct read of quote metadata; changes follow the source path.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  cachedDerivativeNoPropagation: {
    domainOwner: 'derived_cache' as DomainOwner,
    changePropagation: 'no_propagation_required' as ChangePropagation,
    propagationRationale:
      'Reads a stored cached derivative (PNG URL) already saved on the line. Consumers embed the URL as-is; no mapper propagation applies. Re-export after design changes is an operational save step, not a registry drift risk.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  canonicalPayloadReference: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale:
      'Authoritative canonical templatePayload reference. This relationship reads geometry without applying a compatibility overlay.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'canonical_reference' as RelationshipKind,
  },
  compatibilityOverlay: {
    domainOwner: 'derived_cache' as DomainOwner,
    changePropagation: 'no_propagation_required' as ChangePropagation,
    propagationRationale:
      'Legacy conditional merge only (non-v2_lite or v2_lite without saved templatePayload). Current v2_lite lines bypass via resolveExportWindowGeneratorPayload / hasAuthoritativeSavedTemplatePayload.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'compatibility_overlay' as RelationshipKind,
  },
  legacyKvSeparatePath: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'no_propagation_required' as ChangePropagation,
    propagationRationale:
      'Legacy flat KV fallback in visualDesignerTemplate when summaryRows empty. Normal v2_lite staff PDFs use getDesignerSummaryRows instead.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  omittedField: {
    domainOwner: 'quote' as DomainOwner,
    changePropagation: 'no_propagation_required' as ChangePropagation,
    propagationRationale:
      'Intentional public boundary: field excluded from customer payload by the sanitizeVisualMeta allow-list in publicVisualMeta.ts. Staff views read full meta_json.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  ownFormatterSeparateUpdate: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'separate_update_required' as ChangePropagation,
    propagationRationale:
      'This consumer reads and labels the field with its own code instead of getDesignerSummaryRows. A change to the canonical field or its wording must be repeated here by hand.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  inMemoryBasketProjection: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'separate_update_required' as ChangePropagation,
    propagationRationale:
      'visualDesignerLinesToWindowBasketItems maps these source fields to basket item fields and wording with its own code. Changes to getDesignerSummaryRows reach only the designerSummaryRows carried on the item; these fields must be updated here by hand.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  modalWorkingSession: {
    domainOwner: 'derived_cache' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale:
      'Unsaved workingPayload session state. Preview, Configure modal, Save, and PNG generation share the same in-memory payload until persisted.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'standard' as RelationshipKind,
  },
  cardBlankDimensionVsExport: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'no_propagation_required' as ChangePropagation,
    propagationRationale:
      'Legacy compact-card path only when hasAuthoritativeSavedTemplatePayload is false. Current v2_lite saved lines read templatePayload directly.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'compatibility_overlay' as RelationshipKind,
  },
  authoritativeSavedPayload: {
    domainOwner: 'canonical_window' as DomainOwner,
    changePropagation: 'automatic_shared' as ChangePropagation,
    propagationRationale:
      'Accepted saved templatePayload is authoritative for compact-card live preview and export/PDF on current v2_lite Window Generator lines.',
    safeguard: 'none' as SafeguardKind,
    relationshipKind: 'canonical_reference' as RelationshipKind,
  },
} as const satisfies Record<string, Omit<SubscriptionRelationshipProfile, 'currentReadSource'>>;

export type RelationshipPresetKey = keyof typeof RELATIONSHIP_PRESETS;

export function relationshipPreset(
  key: RelationshipPresetKey,
  currentReadSource: string,
  domainOwnerOverride?: DomainOwner,
): SubscriptionRelationshipProfile {
  const base = RELATIONSHIP_PRESETS[key];
  return {
    ...base,
    domainOwner: domainOwnerOverride ?? base.domainOwner,
    currentReadSource,
  };
}

export function isValidDomainOwner(value: string): value is DomainOwner {
  return value === 'canonical_window' || value === 'quote' || value === 'derived_cache';
}

export function isValidChangePropagation(value: string): value is ChangePropagation {
  return (
    value === 'automatic_shared' ||
    value === 'separate_update_required' ||
    value === 'generated_from_shared_contract' ||
    value === 'no_propagation_required' ||
    value === 'unknown'
  );
}

export function isValidSafeguard(value: string): value is SafeguardKind {
  return value === 'contract_parity_test' || value === 'none';
}

export function isValidRelationshipKind(value: string): value is RelationshipKind {
  return value === 'standard' || value === 'canonical_reference' || value === 'compatibility_overlay';
}
